import { FC, useEffect, useState, useRef } from "react";
import { BaseModalWindow } from "../../../../components/modalWindows/BaseModalWindow";
import { useTranslation } from "react-i18next";
import { InvoiceDataProvider } from "../../../../Services/DataProviders/InvoiceDataProvider";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { IInvoiceCorrectSum, IInvoiceGetDTO } from "../../../../libs/coreapi-dto/documents/invoice";
import FlexRow from "../../../../components/controls/FlexRow";
import styles from '../styles/CorrectSumModal.module.scss'
import { DecimalInputV2 } from "../../../../components/controls/inputs/BaseInput";
import { useUserContext } from "../../../../system/providers/userContextProvider";
import { checkLockStatus } from "../../../CommonHelperFunctions";

interface ICorrectSumWindowProps {
    readonly idDocumentGlobal: string
    readonly cancel: () => void
    readonly ok: () => void
}

export const CorrectSumModal : FC<ICorrectSumWindowProps> = (props) => {
    const appContext = useAppContext();
    const userContext = useUserContext();
    const invoiceDP = new InvoiceDataProvider(appContext.coreApiService);
    const [data,setData] = useState<IInvoiceGetDTO>()
    const [disabled, setDisabled] = useState<boolean>(false)
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const isAllowCorrectSumAboveOneRuble = checkLockStatus("AllowCorrectSumAboveOneRuble" as IPermission, userContext.userPermission);      

    const [correctSum, setCorrectSum] = useState<IInvoiceCorrectSum>({supplierSum: 0, supplierSumIncVat: 0, supplierVatSum: 0})
    const [validationErrors, setValidationErrors] = useState({
        supplierSum: '',
        supplierVatSum: '',
        supplierSumIncVat: ''
    });
    
    // Добавляем ref для хранения оригинальных значений
    const originalValuesRef = useRef({
        supplierSum: 0,
        supplierVatSum: 0,
        supplierSumIncVat: 0
    });

    useEffect(() => {
        invoiceDP.getById(props.idDocumentGlobal, setData)
    },[])

    useEffect(() => {
        if (data?.supplierCostInfo) {
            const newCorrectSum = {
                supplierSum: data.supplierCostInfo.sum,
                supplierSumIncVat: data.supplierCostInfo.sumIncVat,
                supplierVatSum: data.supplierCostInfo.vatSum
            };
            setCorrectSum(newCorrectSum);
            
            // Сохраняем оригинальные значения в ref
            originalValuesRef.current = {
                supplierSum: data.supplierCostInfo.sum,
                supplierVatSum: data.supplierCostInfo.vatSum,
                supplierSumIncVat: data.supplierCostInfo.sumIncVat
            };
        }

        const fullLength = data?.items.length
        const gnvlsLength = data?.items.filter(c => c.isGnvls).length 
        if (fullLength && gnvlsLength) setDisabled(fullLength == gnvlsLength)
        
    },[data])

    useEffect(() => {
        setIsSubmitting(true)
        setTimeout(() => {
            setIsSubmitting(false)
        }, 550);
    },[correctSum])

    const validateSumChange = (fieldName: string, newValue: number): string => {
        if (!isAllowCorrectSumAboveOneRuble) {
            // Режим 10 рублей
            const originalValue = originalValuesRef.current[fieldName as keyof typeof originalValuesRef.current];
            const difference = Math.abs(newValue - originalValue);
            if (difference > 10) {
                return `Изменение суммы не может превышать 10 рублей в любую сторону. Текущее изменение: ${difference.toFixed(2)} руб.`;
            }
        }
        return '';
    };

    const handleSumChange = (fieldName: string, value: number, setter: (value: number) => void) => {
        // Проверяем ограничение на изменение
        const error = validateSumChange(fieldName, value);
        setValidationErrors(prev => ({ ...prev, [fieldName]: error }));
        
        // Разрешаем ввод любого значения, но показываем ошибку
        setter(value);
    };

    const handleSumBlur = (fieldName: string, currentValue: number, setter: (value: number) => void) => {
        const originalValue = originalValuesRef.current[fieldName as keyof typeof originalValuesRef.current];
        
        if (isAllowCorrectSumAboveOneRuble) {
            // Старая логика для режима "только копейки" (isAllowCorrectSumAboveOneRuble = true)
            if (Math.floor(currentValue) !== Math.floor(originalValue)) {
                setter(originalValue);
                setValidationErrors(prev => ({ ...prev, [fieldName]: '' }));
            }
        } else {
            // Новая логика для режима "10 рублей в любую сторону" (isAllowCorrectSumAboveOneRuble = false)
            const difference = Math.abs(currentValue - originalValue);
            if (difference > 10) {
                // Если превысили лимит в 10 рублей, возвращаем исходное значение
                setter(originalValue);
                setValidationErrors(prev => ({ ...prev, [fieldName]: '' }));
            } else {
                // Очищаем ошибку валидации если она была
                setValidationErrors(prev => ({ ...prev, [fieldName]: '' }));
            }
        }
    };

    const isOkDisabled = (): boolean => {
        if (isSubmitting || disabled) return true;
        
        // Проверяем наличие ошибок валидации
        return Object.values(validationErrors).some(error => error !== '');
    };

    const { t } = useTranslation();
    const baseT = (value: string) => t('documents.invoice.correctSum.' + value)

    return ( 
        <BaseModalWindow
        header={baseT("header")}
        ok={{
            onClick: () => {
                setTimeout(() => {
                    invoiceDP.correctSum(data?.idGlobal as string, correctSum, (response) => {
                        props.cancel();
                    })
                }, 150);
            },
            sendRequestSpinner: isSubmitting,
            disabled: isOkDisabled()
        }}
        cancel={{
            onClick: () => {
            props.cancel();
            },
        }}
        >
            <label>{baseT("mnemocode")}: {data?.mnemocode}</label>
            <label>{baseT("dateDocument")}: {data?.documentDate?.toLocaleString()}</label>
            <label>{baseT("supplierDocNumber")}: {data?.supplierDocNumber}</label>
            <label>{baseT("supplierDocDate")}: {data?.supplierDocDate?.toLocaleString()}</label>
            {disabled && <label style={{color: "red"}}>{baseT("disabled")}</label>}
            {!isAllowCorrectSumAboveOneRuble ? (
                <label style={{color: "orange", fontSize: "12px"}}>
                    {baseT("increaseDecreaseLimitMessage") || "Максимальное изменение суммы: 10 рублей в любую сторону (увеличение или уменьшение)"}
                </label>
            ) : (
                <label style={{color: "orange", fontSize: "12px"}}>
                    {baseT("kopecksOnlyMessage") || "Корректировка возможна только в пределах копеек (целая часть не должна меняться)"}
                </label>
            )}
            <br></br>
            <FlexRow>
            <div>
                     <section className="content-section">
                        <fieldset className={styles.fieldSet}>
                            <legend className={styles.legend}>{baseT("correctValue")}</legend>
                                <DecimalInputV2
                                    onBlur={(value) => handleSumBlur(
                                        'supplierSum',
                                        value, 
                                        (val) => setCorrectSum(prev => ({...prev, supplierSum: val}))
                                    )}
                                    disabled={disabled}
                                    value={correctSum?.supplierSum} 
                                    onChange={(value) => handleSumChange(
                                        'supplierSum',
                                        value, 
                                        (val) => setCorrectSum(prev => ({...prev, supplierSum: val}))
                                    )}
                                    inline label={baseT("supplierSum")}
                                    error={validationErrors.supplierSum}
                                />
                                <DecimalInputV2 
                                    onBlur={(value) => handleSumBlur(
                                        'supplierVatSum',
                                        value, 
                                        (val) => setCorrectSum(prev => ({...prev, supplierVatSum: val}))
                                    )}
                                    disabled={disabled}
                                    value={correctSum?.supplierVatSum}
                                    onChange={(value) => handleSumChange(
                                        'supplierVatSum',
                                        value, 
                                        (val) => setCorrectSum(prev => ({...prev, supplierVatSum: val}))
                                    )}
                                    inline label={baseT("supplierVatSum")}
                                    error={validationErrors.supplierVatSum}
                                />
                                <DecimalInputV2
                                    onBlur={(value) => handleSumBlur(
                                        'supplierSumIncVat',
                                        value, 
                                        (val) => setCorrectSum(prev => ({...prev, supplierSumIncVat: val}))
                                    )}
                                    disabled={disabled}
                                    value={correctSum?.supplierSumIncVat} 
                                    onChange={(value) => handleSumChange(
                                        'supplierSumIncVat',
                                        value, 
                                        (val) => setCorrectSum(prev => ({...prev, supplierSumIncVat: val}))
                                    )}
                                    inline label={baseT("supplierSumIncVat")}
                                    error={validationErrors.supplierSumIncVat}
                                />
                        </fieldset>
                    </section>
            </div>

            <div>
                     <section className="content-section">
                        <fieldset className={styles.fieldSet}>
                            <legend className={styles.legend}>{baseT("originalValue")}</legend>
                                <DecimalInputV2 value={data?.supplierCostInfo.sum} disabled inline label={baseT("supplierSum")}/>
                                <DecimalInputV2 value={data?.supplierCostInfo.vatSum} disabled inline label={baseT("supplierVatSum")}/>
                                <DecimalInputV2 value={data?.supplierCostInfo.sumIncVat} disabled inline label={baseT("supplierSumIncVat")}/>
                        </fieldset>
                    </section>
            </div>
            </FlexRow>
          
        </BaseModalWindow>)
}